import { lazy, Suspense, useState } from "react";
import { CalendarDays, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "@/context/LanguageContext";

const loadCalendar = () => import("@/components/DateRangeCalendar");
const DateRangeCalendar = lazy(loadCalendar);

const fromIso = (s) => (s ? new Date(s + "T00:00:00") : undefined);
const pad = (n) => String(n).padStart(2, "0");
const fmt = (d) => (d ? `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}` : "");

/** An-/Abreise-Feld: Der Kalender (react-day-picker + date-fns, ~80 kB) wird erst beim Öffnen nachgeladen – bei Hover/Fokus vorgeladen.
 *  availability (optional, aus buildAvailability): halbe Tage für An-/Abreise anderer Gäste + Prüfung der Auswahl.
 *  prop/settings (optional): nach Wahl der Anreise sind zu kurze Abreisetage (Mindestaufenthalt) gesperrt + Hinweis im Fuß. */
export const DateRangeField = ({ checkin, checkout, onChange, labelClass = "text-white", disabled = [], months = 2, side, align = "start", availability, single = false, prop, settings, gaps }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const range = { from: fromIso(checkin), to: fromIso(checkout) };

  const Field = ({ label, value, testid }) => (
    <div className="flex-1 min-w-0">
      <label className={`block text-[11px] font-semibold uppercase tracking-widest mb-1.5 ${labelClass}`}>{label}</label>
      <div className={`h-10 rounded-md border border-stone-300 bg-white px-3 flex items-center gap-2 text-sm cursor-pointer hover:border-brand ${open ? "ring-1 ring-brand border-brand" : ""}`} data-testid={testid}>
        <CalendarDays className="w-4 h-4 text-brand shrink-0" />
        <span className={`truncate ${value ? "text-stone-900" : "text-stone-500"}`}>{value || t.hero.selectDate}</span>
      </div>
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="flex gap-3 col-span-2" role="button" tabIndex={0} onPointerEnter={loadCalendar} onFocus={loadCalendar} data-testid="date-range-trigger">
          {single
            ? <Field label={t.prices.period} value={range.from ? `${fmt(range.from)} – ${range.to ? fmt(range.to) : "…"}` : ""} testid="hero-search-period-input" />
            : <>
                <Field label={t.hero.checkin} value={fmt(range.from)} testid="hero-search-checkin-input" />
                <Field label={t.hero.checkout} value={fmt(range.to)} testid="hero-search-checkout-input" />
              </>}
        </div>
      </PopoverTrigger>
      <PopoverContent align={align} side={side} avoidCollisions={!side} collisionPadding={12} sideOffset={6} className="w-auto max-w-[calc(100vw-1.5rem)] p-0 bg-white border-line shadow-2xl rounded-2xl overflow-hidden" data-testid="date-range-popover">
        <Suspense fallback={<div className="flex items-center justify-center w-[300px] h-[320px] text-stone-400" data-testid="date-range-loading"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
          <DateRangeCalendar range={range} onChange={onChange} close={() => setOpen(false)} months={months} disabled={disabled} availability={availability} prop={prop} settings={settings} gaps={gaps} />
        </Suspense>
      </PopoverContent>
    </Popover>
  );
};
